/**
 * Maquetación y dibujo de la carta del ratón sobre un canvas.
 *
 * El canvas es deliberado: el padre se lleva una imagen que puede guardar en el
 * carrete, mandar por WhatsApp o imprimir. `window.print()` a secas no servía —
 * este tráfico llega de móvil a las 22:00 y imprimir desde el móvil es justo lo
 * que peor hace un móvil.
 *
 * La maquetación (`wrapLines`, `layoutLetter`) es pura y recibe la función de
 * medida inyectada, así que se testea sin canvas. El dibujo es el único trozo
 * que toca el contexto 2D. Sin dependencias nuevas.
 */

import type { ComposedLetter } from './tooth-letter'

/** Mide el ancho de un texto con una fuente dada. La aporta el canvas real. */
export type Measure = (text: string, font: string) => number

export type LetterBlock = {
  text: string
  font: string
  x: number
  y: number
  align: 'left' | 'right'
}

export type LetterLayout = {
  width: number
  height: number
  blocks: LetterBlock[]
}

/** Lienzo pensado para leerse en móvil y para imprimirse en A5 sin pixelarse. */
export const LETTER_CANVAS = {
  width: 1000,
  padding: 90,
  bodyFont: '30px Georgia, "Times New Roman", serif',
  greetingFont: 'bold 38px Georgia, "Times New Roman", serif',
  signatureFont: 'italic 36px Georgia, "Times New Roman", serif',
  lineHeight: 46,
  paragraphGap: 24,
  paper: '#fdf8ec',
  ink: '#3f3222',
} as const

/**
 * Parte un texto en líneas que caben en `maxWidth`.
 *
 * Una palabra más larga que el ancho disponible se deja sola en su línea en vez
 * de trocearla: preferimos que sobresalga un nombre muy largo a partirlo por la
 * mitad. Nunca devuelve líneas vacías, así que no abre huecos en la carta.
 */
export function wrapLines(
  text: string,
  font: string,
  maxWidth: number,
  measure: Measure
): string[] {
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length === 0) return []

  const lines: string[] = []
  let current = words[0]

  for (const word of words.slice(1)) {
    const candidate = `${current} ${word}`
    if (measure(candidate, font) <= maxWidth) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  }
  lines.push(current)
  return lines
}

/**
 * Coloca saludo, párrafos, despedida y firma, y devuelve el alto total que
 * necesita el canvas. La firma va a la derecha, como en una carta de verdad.
 */
export function layoutLetter(letter: ComposedLetter, measure: Measure): LetterLayout {
  const { width, padding, bodyFont, greetingFont, signatureFont, lineHeight, paragraphGap } =
    LETTER_CANVAS
  const maxWidth = width - padding * 2
  const blocks: LetterBlock[] = []
  let y = padding + lineHeight

  function pushWrapped(text: string, font: string, align: 'left' | 'right' = 'left') {
    for (const line of wrapLines(text, font, maxWidth, measure)) {
      blocks.push({ text: line, font, x: align === 'left' ? padding : width - padding, y, align })
      y += lineHeight
    }
  }

  pushWrapped(letter.greeting, greetingFont)
  y += paragraphGap

  for (const paragraph of letter.paragraphs) {
    pushWrapped(paragraph, bodyFont)
    y += paragraphGap
  }

  pushWrapped(letter.signoff, bodyFont)
  y += paragraphGap
  pushWrapped(letter.signature, signatureFont, 'right')

  return { width, height: y + padding - lineHeight + paragraphGap, blocks }
}

/**
 * Pinta la carta en el canvas y lo deja dimensionado al contenido.
 *
 * `scale` sube la resolución del bitmap sin cambiar la maqueta, para que la
 * imagen descargada se vea nítida en pantallas densas y al imprimir.
 */
export function drawLetter(
  canvas: HTMLCanvasElement,
  letter: ComposedLetter,
  scale = 2
): LetterLayout {
  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas 2d context unavailable')

  const measure: Measure = (text, font) => {
    context.font = font
    return context.measureText(text).width
  }
  const layout = layoutLetter(letter, measure)

  canvas.width = layout.width * scale
  canvas.height = layout.height * scale
  context.setTransform(scale, 0, 0, scale, 0, 0)

  context.fillStyle = LETTER_CANVAS.paper
  context.fillRect(0, 0, layout.width, layout.height)

  context.fillStyle = LETTER_CANVAS.ink
  context.textBaseline = 'alphabetic'
  for (const block of layout.blocks) {
    context.font = block.font
    context.textAlign = block.align
    context.fillText(block.text, block.x, block.y)
  }

  return layout
}

/** Exporta el canvas a PNG. Rechaza si el navegador no da blob. */
export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas.toBlob returned null'))
    }, 'image/png')
  })
}
